import { FC } from 'react';

import { BugoData } from '@shared/api/bugo/bugo.interface';
import { DeathCharacter } from '@shared/miscValues';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatFeventDateString } from '@shared/utils/formatDate';

interface Props {
  bugo: BugoData;
}

//* 고인 표기 "故 ○○○ 상세 (남/82세)"
//* - 故는 訃告와 같은 바탕체(서브셋 3글자 중 하나)로 그린다 — 본문 글꼴엔 한자가 없어 대체 글꼴로 빠지면 어색함
//* - 이름 상세는 회색·성별/나이는 괄호(build-prod-preed 836b303a 인라인 JSX 레이아웃을 develop으로 수렴,
//*   flex 정렬은 공백 문자를 삼켜 inline+align-middle로 대체)
const DeceasedNameHeading: FC<{ deceasedInfo: BugoData['fevent']['deceasedInfo'] }> =
  function DeceasedNameHeading({ deceasedInfo }) {
    const { name, nameDetail, sex, age } = deceasedInfo;
    //* 띄어쓰기는 마진이 아니라 실제 공백 문자로 — 복사·스크린리더에서 "故이름상세"로 붙지 않게
    //* 故 1.07em: 바탕체 한자가 Spoqa 한글보다 7% 작아(0.86em vs 0.92em) 같은 크기면 위가 비어 낮아 보임 — 키우면 위·아래·중심 일치
    return (
      <>
        <span className="batang text-[1.07em]">{DeathCharacter}</span> {name}
        {nameDetail && (
          <>
            {' '}
            <span className="align-middle text-17 font-medium text-gray-300">
              {nameDetail}
            </span>
          </>
        )}
        {(sex || age) && (
          <>
            {' '}
            <span className="align-middle font-normal">
              ({sex ? sex : ''}
              {sex && age ? '/' : ''}
              {age ? `${age}세` : ''})
            </span>
          </>
        )}
      </>
    );
  };

export const ObituaryHeader: FC<Props> = function ObituaryHeader({ bugo }: Props) {
  if (bugo.fevent.deceasedInfo.dpImage) {
    return (
      <div className="h-32 bg-gray-800 py-3 px-6">
        <div className="flex h-full gap-6">
          <img
            className="h-full rounded-sm bg-gradient-to-t from-gray-50 to-gray-400"
            src={fileBucketUrl(bugo.fevent.deceasedInfo.dpImage.url, 'w400')}
          ></img>
          <div className="flex-1 space-y-3 pt-1">
            <p className="batang text-left text-30 font-bold text-white">訃告</p>
            <div className="space-y-2 text-left text-white">
              <p className="text-19 font-bold leading-5">
                <DeceasedNameHeading deceasedInfo={bugo.fevent.deceasedInfo} />
              </p>
              {bugo.fevent.deceasedInfo.death?.date && (
                <p className="text-14 leading-5">
                  {formatFeventDateString(bugo.fevent.deceasedInfo.death, false)}&nbsp;
                  {bugo.fevent.deceasedInfo.bugoTerm
                    ? bugo.fevent.deceasedInfo.bugoTerm
                    : '별세'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="h-32 space-y-3 bg-gray-800 pt-4">
        <p className="batang text-center text-30 font-bold text-white">訃告</p>
        <div className="space-y-2 text-center text-white">
          <p className="text-19 font-bold leading-5">
            <DeceasedNameHeading deceasedInfo={bugo.fevent.deceasedInfo} />
          </p>
          {bugo.fevent.deceasedInfo.death?.date && (
            <p className="text-14 leading-5">
              {formatFeventDateString(bugo.fevent.deceasedInfo.death, false)}&nbsp;
              {bugo.fevent.deceasedInfo.bugoTerm
                ? bugo.fevent.deceasedInfo.bugoTerm
                : '별세'}
            </p>
          )}
        </div>
      </div>
    );
  }
};
